import type { AxiosResponse } from "axios";

import { getData, postData } from "@/apis/apiInstance";

import type {
  AddCheckInScheduleFeedbackPayloadType,
  AddFeedbackPerformanceEvaluationPayloadType,
  AddPerformanceEvaluationPayloadType,
  AddResignationRequestPayloadType,
  GetAttendanceAveragePayloadType,
  GetAverageJobRatingPayloadType,
  GetCheckInScheduleListPayloadType,
  GetManagersCheckInScheduleSlotsPayloadType,
  GetMySpacePeopleOnLeavePayloadType,
  GetMySpaceUpcomingEventsDataPayloadType,
  GetMySpaceViewTeamMembersPayloadType,
  GetPerformanceEvaluationPayloadType,
  GetResignationDetailsPayloadType,
  GetSingleCheckInScheduleDetailsPayloadType,
  GetSinglePerformanceEvaluationPayloadType,
  UpdateCheckInSchedulePayloadType,
} from "@/commonTypes/mySpaceType";

import { getCSRFToken, reverseDate } from "@/utils/commonFunctions";

export async function getMySpaceAnniversaryAndUpcomingBirthdayApi(
  data: GetMySpaceUpcomingEventsDataPayloadType
): Promise<AxiosResponse> {
  const { token, limit, offset, event_type } = data;
  let finalParams = {
    limit,
    offset,
    event_type,
  };
  return await getData(`finac-crm/upcoming/events`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getMySpacePeopleOnLeaveApi(
  data: GetMySpacePeopleOnLeavePayloadType
): Promise<AxiosResponse> {
  const { token, limit, offset } = data;
  let finalParams = {
    limit,
    offset,
  };
  return await getData(`finac-crm/employee-on-leave/today`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getAttendanceAverageApi(
  data: GetAttendanceAveragePayloadType
): Promise<AxiosResponse> {
  const { token, start_date, end_date, user_id } = data;
  let finalParams = {
    start_date,
    end_date,
    user_id,
  };
  return await getData(`attendance/average`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

export async function getCheckInScheduleListApi(
  data: GetCheckInScheduleListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    role,
    offset,
    limit,
    userId,
    presentTime,
    notificationId,
    startDate,
    endDate,
    filterManager,
    location,
    employmentStatus,
    sortBy,
    sortingOrder,
  } = data;
  let finalParams = {
    role,
    offset,
    limit,
    user_id: userId,
    present_time: presentTime,
    notification_id: notificationId ? notificationId : null,
    is_page_seen: notificationId ? true : false,
    start_date: startDate ? reverseDate(startDate) : null,
    end_date: endDate ? reverseDate(endDate) : null,
    manager: filterManager,
    branch: location,
    employment_status: employmentStatus,
    sort_by: sortBy,
    sort_order: sortingOrder,
  };
  return await getData(`finac-crm/check-in/list`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getSingleCheckInScheduleDetailsApi(
  data: GetSingleCheckInScheduleDetailsPayloadType
): Promise<AxiosResponse> {
  const { token, checkInId } = data;
  let finalParams = {
    check_in_id: checkInId,
  };
  return await getData(`finac-crm/check-in`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getManagersAvailableCheckInScheduleSlotsDetailsApi(
  data: GetManagersCheckInScheduleSlotsPayloadType
): Promise<AxiosResponse> {
  const { token, userId, date } = data;
  let finalParams = {
    user_id: userId,
    date,
  };
  return await getData(`finac-crm/user/booked-slots`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function addCheckInScheduleApi(
  data: UpdateCheckInSchedulePayloadType
): Promise<AxiosResponse> {
  const { token, manager, date, startTime } = data;

  return await postData(
    `finac-crm/check-in/add`,
    {
      manager: manager,
      date: reverseDate(date),
      start_time: startTime?.trim(),
    },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function updateCheckInScheduleApi(
  data: UpdateCheckInSchedulePayloadType
): Promise<AxiosResponse> {
  const { token, manager, date, startTime, checkInId } = data;
  return await postData(
    `finac-crm/check-in/update`,
    {
      manager: manager,
      date: reverseDate(date),
      start_time: startTime?.trim(),
      check_in_id: checkInId,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}
export async function addCheckInScheduleFeedbackApi(
  data: AddCheckInScheduleFeedbackPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    checkInId,
    feedback,
    questionOne,
    questionTwo,
    questionThree,
    questionOneComment,
    questionTwoComment,
    questionThreeComment,
  } = data;
  return await postData(
    `finac-crm/check-in/feedback/add`,
    {
      check_in_id: checkInId,
      feedback: feedback,
      question_1: questionOne,
      question_1_comment: questionOneComment,
      question_2: questionTwo,
      question_2_comment: questionTwoComment,
      question_3: questionThree,
      question_3_comment: questionThreeComment,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function downloadCheckInsReportsApi(
  data: GetCheckInScheduleListPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    role,
    offset,
    limit,
    userId,
    startDate,
    endDate,
    filterManager,
    location,
    employmentStatus,
  } = data;
  let finalParams = {
    role,
    offset,
    limit,
    user_id: userId,
    start_date: startDate,
    end_date: endDate,
    manager: filterManager,
    branch: location,
    employment_status: employmentStatus,
  };
  return await getData(`finac-crm/check-in/report/download`, {
    params: {
      ...finalParams,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
// pe form
export async function getPerformanceEvaluationListApi(
  data: GetPerformanceEvaluationPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    role,
    offset,
    limit,
    notificationId,
    userId,
    startDate,
    endDate,
    filterManager,
    sortBy,
    sortByValue,
  } = data;
  let finalParams = {
    role,
    offset,
    limit,
    notification_id: notificationId ? notificationId : null,
    is_page_seen: notificationId ? true : false,
    user: userId,
    start_date: startDate ? reverseDate(startDate) : null,
    end_date: endDate ? reverseDate(endDate) : null,
    manager: filterManager,
    sort_by: sortBy,
    sorting_order: sortByValue,
  };
  return await getData(`finac-crm/performance-evaluation/list`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function addPerformanceEvaluationApi(
  data: AddPerformanceEvaluationPayloadType
): Promise<AxiosResponse> {
  const { token, title, manager, startDate, endDate, hours, attachments } =
    data;

  return await postData(
    `finac-crm/performance-evaluation/add`,
    {
      title: title,
      manager: manager,
      start_date: startDate ? reverseDate(startDate) : "",
      end_date: endDate ? reverseDate(endDate) : "",
      hours: hours,
      attachments: attachments,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function getSinglePerformanceEvaluationDetailsApi(
  data: GetSinglePerformanceEvaluationPayloadType
): Promise<AxiosResponse> {
  const { token, performanceEvaluationId } = data;
  let finalParams = {
    performance_evaluation_id: performanceEvaluationId,
  };
  return await getData(`finac-crm/performance-evaluation`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function addPerformanceEvaluationFeedbackApi(
  data: AddFeedbackPerformanceEvaluationPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    performanceEvaluationId,
    feedbackComment,
    performanceRating,
    managerFileAttach,
  } = data;
  return await postData(
    `finac-crm/performance-evaluation/feedback/add`,
    {
      performance_evaluation_id: performanceEvaluationId,
      feedback: feedbackComment,
      rating: performanceRating,
      attachments: managerFileAttach,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

export async function downloadPerformanceEvaluationReportsApi(
  data: GetPerformanceEvaluationPayloadType
): Promise<AxiosResponse> {
  const {
    token,
    role,
    offset,
    limit,
    userId,
    startDate,
    endDate,
    filterManager,
  } = data;
  let finalParams = {
    role,
    offset,
    limit,
    user_id: userId,
    start_date: startDate,
    end_date: endDate,
    manager: filterManager,
  };
  return await getData(`finac-crm/performance-evaluation/report/download`, {
    params: {
      ...finalParams,
    },
    responseType: "blob",
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}

export async function getAveargeJobRatingListApi(
  data: GetAverageJobRatingPayloadType
): Promise<AxiosResponse> {
  const { token, userId } = data;
  let finalParams = {
    user_id: userId,
  };
  return await getData(`finac-crm/job-ratings/average`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
      "x-csrf-token": getCSRFToken(),
    },
  });
}
export async function getMyTeamMembersApi(
  data: GetMySpaceViewTeamMembersPayloadType
): Promise<AxiosResponse> {
  const { token, userId, role } = data;
  let finalParams = {
    user_id: userId,
    role: role,
  };
  return await getData(`finac-crm/users/managed_or_coached`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}

// resignation request

export async function addResignationRequestApi(
  data: AddResignationRequestPayloadType
): Promise<AxiosResponse> {
  const { token, userId, resignationReason, isRevoke } = data;
  return await postData(
    `finac-crm/hrms/resignation/add`,
    {
      user_id: userId,
      resignation_reason: resignationReason,
      is_revoke: isRevoke,
    },
    {
      headers: {
        "Content-Type": "application/json",
        "user-token": token,
        "x-csrf-token": getCSRFToken(),
      },
    }
  );
}

// get resignation details

export async function getResignationDetailsApi(
  data: GetResignationDetailsPayloadType
): Promise<AxiosResponse> {
  const { token, resignationId, isPageSeen, redirectNotificationId } = data;
  const finalParams = {
    resignation_id: resignationId,
    is_page_seen: isPageSeen,
    notification_id: redirectNotificationId,
  };
  return await getData(`finac-crm/hrms/resignation/details`, {
    params: {
      ...finalParams,
    },
    headers: {
      "Content-Type": "application/json",
      "user-token": token,
    },
  });
}
